import { ProtocolMedicationOption } from '@/modules/protocol/protocol.model';
import { useProtocolItemOptions } from '@/modules/protocol/protocol.slice';
import { ProtocolName } from '@/modules/protocol/protocol-status.model';
import { useCallback } from 'react';
import { SentryService } from '@ianesthesie-kit/sentry';
import { throwOnDev } from '@/util';
import { ProtocolService } from '@/modules/protocol';

type MedsFilterFn = (
  payload: Record<string, any>,
  medOptions: ProtocolMedicationOption[]
) => ProtocolMedicationOption[];

const nicotineRecommendations: Record<number, number[]> = {
  7: [7],
  14: [14],
  21: [21],
  35: [14, 21],
  49: [14, 14, 21]
};

const getNicotineRecommendations: MedsFilterFn = (payload, medOptions) => {
  const recommendedNicotineDosage = payload?.recommendedNicotineDosage;

  // Handle falsy values or strings without digits (e.g., "-", "N/A", etc.)
  if (
    !recommendedNicotineDosage ||
    (typeof recommendedNicotineDosage === 'string' &&
      !/\d/.test(recommendedNicotineDosage))
  ) {
    return [];
  }

  const extractNumber = (str: string) => {
    if (!str) {
      return Number.NaN;
    }
    const num = /^\d+/.exec(str)?.[0];
    return num ? +num : Number.NaN;
  };

  const nicotineRecommendation =
    typeof recommendedNicotineDosage === 'string'
      ? extractNumber(recommendedNicotineDosage)
      : +recommendedNicotineDosage;
  if (Number.isNaN(nicotineRecommendation)) {
    const err = new Error(
      `recommendedNicotineDosage is truthy but not a number: ${recommendedNicotineDosage}`
    );
    console.error(err);
    SentryService.getInstance().captureException(err);
    return [];
  }

  return (nicotineRecommendations[nicotineRecommendation] ?? [])
    .map((dosage) => medOptions.find((med) => med.dosage.includes(`${dosage}`)))
    .concat(medOptions.find((med) => med.code === 'NICOTINE_TABLET'))
    .filter(Boolean) as ProtocolMedicationOption[];
};

type GenericMedRecommendationPayload = {
  age?: number;
  weight?: number;
};

const getGenericMedRecommendations: MedsFilterFn = (
  payload: GenericMedRecommendationPayload,
  medOptions
) => {
  const { age, weight } = payload;
  const recommendations = medOptions.filter((opt) => {
    switch (opt.code) {
      case 'PARACETAMOL':
        if (typeof weight !== 'number') {
          return true;
        }
        if (weight < 30) {
          return /^15\s?mg.*/.test(opt.dosage);
        }
        if (weight < 50) {
          return /^500\s?mg.*/.test(opt.dosage);
        }
        return /^1\s?g.*/.test(opt.dosage);

      case 'KETOPROFENE':
        if (typeof age === 'number' && age < 10) {
          return false;
        }
        if (typeof weight === 'number') {
          if (weight < 50) {
            return /^50\s?mg.*/.test(opt.dosage);
          }
          return /^100\s?mg.*/.test(opt.dosage);
        }
        return true;
      default:
        return false;
    }
  });

  const ketoExists = !!recommendations.find(
    (opt) => opt.code === 'KETOPROFENE' && /^100\s?mg.*/.test(opt.dosage)
  );
  const esoExists = !!recommendations.find(
    (opt) => opt.code === 'ESOMEPRAZOLE'
  );
  if (ketoExists && !esoExists) {
    const eso = medOptions.find((opt) => opt.code === 'ESOMEPRAZOLE');
    if (eso) {
      recommendations.push(eso);
    }
  }
  return recommendations.sort(ProtocolService.sortGenericPrescriptionOptions);
};

const isProtocolSupported = (
  protocol?: ProtocolName
): protocol is SupportedProtocol =>
  protocol === ProtocolName.NICOTINE ||
  protocol === ProtocolName.GENERIC_PRESCRIPTION;

type SupportedProtocol =
  | ProtocolName.NICOTINE
  | ProtocolName.GENERIC_PRESCRIPTION;

type MedsRecommendationFn = (
  payload: Record<string, any> // TODO type safe payload
) => ProtocolMedicationOption[];

export const useMedsRecommendation = (
  protocol: ProtocolName
): MedsRecommendationFn => {
  const { data: medicationList } = useProtocolItemOptions<SupportedProtocol>({
    protocol: protocol as SupportedProtocol
  });

  return useCallback(
    (payload) => {
      if (!isProtocolSupported(protocol)) {
        SentryService.getInstance().captureException(
          new Error(`Cannot recommend meds for protocol ${protocol}`)
        );
        throwOnDev('add cases for rest protocols');
        return [];
      }

      if (!medicationList?.length) {
        return [];
      }

      if (protocol === ProtocolName.NICOTINE) {
        return getNicotineRecommendations(payload, medicationList);
      }

      if (protocol === ProtocolName.GENERIC_PRESCRIPTION) {
        return getGenericMedRecommendations(payload, medicationList);
      }

      return [];
    },
    [medicationList, protocol]
  );
};
